import * as Http from '../../api/Http';
import * as AsyncRequest from '../../AsyncRequest';
import * as ContextualServerStateSlice from 'dg-web-shared/lib/ContextualServerStateSlice';
import * as Flux from 'dg-web-shared/lib/Flux';
import { Updater } from 'dg-web-shared/lib/Flux';
import { LicensePlateType } from 'dg-web-shared/dto/LicensePlateType';
import { CheckinDataState } from '../../park-create/components/zone-transaction/ZoneEnforcedCheckin';
import { Message } from 'dg-web-shared/lib/Localized';
import { IntradayValidityWeekProfile } from 'dg-web-shared/model/IntradayValidityWeekProfile';
import { DateTime } from 'luxon';
import { TransactionCost } from 'dg-web-shared/model/TransactionList';
import { Slope } from 'taxomex-shared/tariff/models/TariffDefinition.ts';

export interface LicensePlateDTO {
    id: number;
    licensePlateNr: string;
    type: LicensePlateType;
    country: string;
    remarks: string | null;
}

export namespace Detail {
    export interface State {
        parkTransactionContractExternalId: string | null;
        parkTransactionShowDetail: boolean;

        permitExternalId: string | null;
        purchasedPermitExternalId: string | null;
        permitPurchaseIncludedBalanceTopUp: boolean;

        offstreetCheckinExternalId: string | null;
    }

    export const { set, get, reset, stateWrite } = Flux.generateState<State>(
        'transactions-list/TransactionsListState.Detail',
        {
            parkTransactionContractExternalId: null,
            parkTransactionShowDetail: false,
            permitExternalId: null,
            purchasedPermitExternalId: null,
            permitPurchaseIncludedBalanceTopUp: false,
            offstreetCheckinExternalId: null,
        },
    );
}

export namespace CheckinByPlateDetailState {
    export type AutoTerminationReason = 'no_balance' | 'max_park_time';
    export type LicensePlate = LicensePlateDTO;

    interface SharedCheckinByPlate {
        id: number;
        contractExternalId: string;
        operatorName: string;
        city: string;
        zipCode: string;
        zoneName: string;
        extZoneCode: number;
        licensePlate: LicensePlate;
        begin: string;
        isRunning: boolean;
        currentTime: string;
    }

    export interface RunningCheckinByPlate extends SharedCheckinByPlate {
        isRunning: true;
        tariffMaxEnd: string;
        autoTerminationTime: string;
        autoTerminationPrice: number;
        tariffCurve: Slope[] | null;
        tariffMaxMinutes: number;
        minChfStep: number;
        surchargeData: CheckinDataState.SurchargeData[];
    }

    export interface TerminatedCheckinByPlate extends SharedCheckinByPlate {
        isRunning: false;
        end: string;
        key: string;
        contractId: number;
        cost: TransactionCost;
    }

    export type CheckinByPlate =
        | RunningCheckinByPlate
        | TerminatedCheckinByPlate;
    export type State =
        ContextualServerStateSlice.ContextualState<CheckinByPlate>;

    export const getReferenceTime = (t: CheckinByPlate) =>
        DateTime.fromISO(t.currentTime).startOf('minute');
    export const requestGenerator = (context: Context) =>
        Http.TransactionsList.Detail.ParkTransaction.get(
            context && context.contractExternalId,
        );

    export interface Context {
        contractExternalId: string;
    }

    export const { get, getState, refetchSameContext } =
        ContextualServerStateSlice.generateContextualState<
            Context,
            CheckinByPlate,
            AsyncRequest.RawRequest
        >({
            key: 'transactions-list/TransactionsListState.ParkTransactionDetail',
            requestGenerator,
            request: AsyncRequest.requestV2,
            parseBody: b => b,
            refreshIntervalSecs: 180,
        });
}

export namespace PermitDetail {
    export interface PermitDetailLicensePlate {
        id: number;
        licensePlateNr: string;
        type: LicensePlateType;
        country: string;
        remarks: string | null;
        explicitCheckin:
            | ExplicitCheckinRunning
            | ExplicitCheckinNotRunning
            | null;
        isMissingBadgeButRequired: boolean;
        isMissingVehicleButRequired: boolean;
        pastActivations: Activation[];
    }

    export type Activation = {
        contractId: number;
        validFrom: string;
        validTo: string;
    };

    export interface ExplicitCheckinRunning {
        running: true;
        checkoutPossible: boolean;
        runningFrom: string;
        runningUntil: string;
    }

    export interface ExplicitCheckinNotRunning {
        running: false;
        checkinPossible: boolean;
    }

    interface PermitType {
        description: string;
        zipCode: string;
        id: number;
    }

    interface PermitZone {
        extZoneId: number;
        uniqueId: number;
        name: string;
        city: string;
        zipCode: string;
    }

    interface Badge {
        id: number;
        labelNr: string;
    }

    interface OffstreetZone {
        uniqueId: number;
        zoneId: number;
        name: string;
        city: string;
        zipCode: string;
    }

    export interface MinimalBalanceWarning {
        showMinimalBalanceWarning: boolean;
        isPrepaid: boolean;
        minimalEntryAmount: number;
    }

    export interface ExplicitCheckinInfo {
        usages: UsageInfo | null;
        maxConcurrentCheckins: number | null;
    }

    export interface UsageInfo {
        usageLimit: number;
        usedCount: number;
    }

    export enum PaymentChannel {
        PARKINGPAY = 'PARKINGPAY',
        PARKINGPORTAL_COUNTER = 'PARKINGPORTAL_COUNTER',
    }

    export interface Permit {
        id: number;
        contractExternalId: string;
        urlKey: string;
        operatorId: number;
        city: string;
        additionalInfo: string;
        operatorName: string;
        validFrom: DateTime;
        validTo: DateTime;
        isValid: boolean;
        permitType: PermitType;
        price: number;
        permitZones: PermitZone[];
        licensePlates: PermitDetailLicensePlate[];
        runningLpInfo: string | null;
        isRenewable: boolean;
        isRenewableExact: boolean;
        missingLpsForRenewal: LicensePlateDTO[];
        badges: Badge[];
        offstreetZones: OffstreetZone[];
        paymentChannel: PaymentChannel;
        minimalBalanceWarning: MinimalBalanceWarning;
        explicitCheckinInfo: ExplicitCheckinInfo | null;
        intradayValidityWeekProfile: IntradayValidityWeekProfile | null;
        operatorEditPolicyText: Message;
        permitIsChangeable: boolean;
        operatorContactDataMarkdown: string | null;
        showMissingVehicleWarning: boolean;
        showMissingBadgeWarning: boolean;
        badgeRequired: boolean;
    }

    interface PermitUnparsed extends Omit<Permit, 'validFrom' | 'validTo'> {
        validFrom: string;
        validTo: string;
    }

    const parsePermit = (t: PermitUnparsed): Permit =>
        Object.assign(t, {
            validFrom: DateTime.fromISO(t.validFrom),
            validTo: DateTime.fromISO(t.validTo),
        });

    export type State = ContextualServerStateSlice.ContextualState<Permit>;

    interface Context {
        contractExternalId: string;
    }

    export const { get, getState, refetchSameContext } =
        ContextualServerStateSlice.generateContextualState<
            Context,
            Permit,
            AsyncRequest.RawRequest
        >({
            key: 'transactions-list/TransactionsListState.PermitDetail',
            requestGenerator: (context: Context) =>
                Http.TransactionsList.Detail.Permit.get(
                    context && context.contractExternalId,
                ),
            request: AsyncRequest.requestV2,
            parseBody: b => parsePermit(b),
        });
}

export namespace OffstreetCheckinDetail {
    export interface OffstreetCheckin {
        id: number;
        operatorName: string;
        city: string;
        remarks: string;
        zipCode: string;
        zoneName: string;
        badgeLabelNr: string;
        begin: DateTime;
        end: DateTime;
        price: number;
        key: string;
        contractId: number;
        cost: TransactionCost;
    }

    interface OffstreetCheckinUnparsed extends Omit<
        OffstreetCheckin,
        'begin' | 'end'
    > {
        begin: string;
        end: string;
    }

    const parseOffstreetTransactionDetail = (
        t: OffstreetCheckinUnparsed,
    ): OffstreetCheckin =>
        Object.assign(t, {
            begin: DateTime.fromISO(t.begin),
            end: DateTime.fromISO(t.end),
        });
    export type State =
        ContextualServerStateSlice.ContextualState<OffstreetCheckin>;

    interface Context {
        contractExternalId: string;
    }

    export const { get, getState } =
        ContextualServerStateSlice.generateContextualState<
            Context,
            OffstreetCheckin,
            AsyncRequest.RawRequest
        >({
            key: 'transactions-list/TransactionsListState.OffstreetTransactionDetail',
            requestGenerator: (context: Context) =>
                Http.TransactionsList.Detail.OffstreetTransaction.get(
                    context && context.contractExternalId,
                ),
            request: AsyncRequest.requestV2,
            parseBody: b => parseOffstreetTransactionDetail(b),
        });
}

export function updateTransactionlistWarnings(update: Updater) {
    update(s => {
        PermitDetail.refetchSameContext(s, false);
        return 'update-permit-warning-transactionlist';
    });
}
